import React, { useState } from 'react'
import Addresses from '../../../components/site/Addresses'
import Email from './email'
import { sections, addresses, contact } from '../../../content/site'
import { buildMailtoUrl } from './buildMailto'

// Contact form, on the new design system (Task 10). Rebuilt directly on labelled inputs
// rather than reused via the old Primaryinput/PrimarytextArea/Primaryaltbtn components:
// those render a <label> as a plain sibling of the <input>, with no htmlFor/id pairing, and
// the task brief requires a real association ("not an aria-label standing in for one"). All
// four fields, their exact labels (including "You thoughts", the live copy, not a typo
// introduced here) and the Submit control survive; only the markup/CSS changed.
//
// The old submit button had no `Action` wired to it at all (Task 10's own finding, see
// task-10-report.md) - the enquiry was silently discarded. Per the task's ruling, Submit now
// composes a mailto: to info@nvisiondental.com carrying the four fields (buildMailto.js),
// the same destination src/pages/public/contact/email.jsx already sends to. Required-field
// validation is new (the old form had none): empty fields block the compose and show an
// inline message next to the field, in the interface's own voice, not a browser alert or a
// native `required` popup (the <form> is `noValidate` so no native bubble can appear). After
// a successful compose, a status line tells the visitor their mail client should now be open
// - a mail client opening with no on-page acknowledgement looks like a bug, not a feature.
//
// The branch list reuses <Addresses/> (Task 5) rather than duplicating the three offices'
// street/phone/email a second time, per the task brief. Addresses renders as a standalone,
// full-bleed, centered section by design (Home, About); nesting it in the narrower half of a
// two-column grid needs presentation-only overrides (stacked cards, left-aligned heading) -
// scoped under .contact-branches in site.css's CONTACT banner, same pattern
// .portal-section/.addresses-section already use to retint shared primitives for their own
// context. No JSX or address data is duplicated; only the component's own classes are
// re-targeted by ancestor selector.
//
// Task 10 fix round 1 (review finding, Important): at 1440px the address column (3 stacked
// cards) is much taller than the form column, leaving ~600px of dead white space below
// Submit - the first thing a visitor notices scrolling past the page's primary element.
// Fixed with layout, not invented content: <Email/> (the existing mailto CTA, previously its
// own top-level section rendered after this one in index.jsx) now renders directly under the
// form inside the same .contact-form-col, giving the form column a natural end instead of a
// void. .contact-grid's ratio also moved from an even 1fr/1fr to 3fr/2fr so the form column
// (this page's primary element, per the task brief) reads as wider than the branch list, not
// equal to it. See task-10-report.md's fix-round section for before/after screenshots.
const Form = () => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [sent, setSent] = useState(false);

    const validate = () => {
        const next = {};
        if (!firstName.trim()) next.firstName = contact.form.errors.firstName;
        if (!lastName.trim()) next.lastName = contact.form.errors.lastName;
        if (!email.trim()) next.email = contact.form.errors.email;
        if (!message.trim()) next.message = contact.form.errors.message;
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSent(false);
        if (!validate()) return;
        window.open(buildMailtoUrl({ firstName, lastName, email, message }), '_blank');
        setSent(true);
    };

    const field = (id, value) => ({
        'aria-invalid': !!errors[id] || undefined,
        'aria-describedby': errors[id] ? `contact-${id}-error` : undefined
    });

    return (
        <section className="section contact-section">
            <div className="container contact-grid">
              <div className="contact-form-col">
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                    <div className="contact-form-row">
                        <div className="form-field">
                            <label htmlFor="contact-firstName">{contact.form.firstNameLabel}</label>
                            <input
                                id="contact-firstName"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                {...field('firstName')}
                            />
                            {errors.firstName && <p className="form-field-error" id="contact-firstName-error">{errors.firstName}</p>}
                        </div>
                        <div className="form-field">
                            <label htmlFor="contact-lastName">{contact.form.lastNameLabel}</label>
                            <input
                                id="contact-lastName"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                {...field('lastName')}
                            />
                            {errors.lastName && <p className="form-field-error" id="contact-lastName-error">{errors.lastName}</p>}
                        </div>
                    </div>

                    <div className="form-field">
                        <label htmlFor="contact-email">{contact.form.emailLabel}</label>
                        <input
                            id="contact-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            {...field('email')}
                        />
                        {errors.email && <p className="form-field-error" id="contact-email-error">{errors.email}</p>}
                    </div>

                    <div className="form-field">
                        <label htmlFor="contact-message">{contact.form.messageLabel}</label>
                        <textarea
                            id="contact-message"
                            rows={5}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            {...field('message')}
                        />
                        {errors.message && <p className="form-field-error" id="contact-message-error">{errors.message}</p>}
                    </div>

                    <button type="submit" className="btn-lg contact-submit">{contact.form.submitLabel}</button>

                    {sent && (
                        <p className="contact-form-status" role="status">{contact.form.success}</p>
                    )}
                </form>

                <Email/>
              </div>

              <div className="contact-branches">
                  <Addresses section={sections.addresses} addresses={addresses} />
              </div>
            </div>
        </section>
    )
}

export default Form
